/* oxlint-disable jsx-a11y/prefer-tag-over-role, jsx-a11y/click-events-have-key-events */
// The ARIA combobox pattern inside a popup: the keys are on the search box (the arrows, Enter,
// Escape) with `aria-activedescendant`, so the options take clicks only.
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { searchByName } from "@teamna/shared";
import { useTrackNameSearch } from "@teamna/web-client/hooks/use-name-search.ts";
import { Avatar, SearchField } from "../../components/index.ts";

/**
 * One coach out of many, in a small space: a chip with the chosen name that opens a list with a
 * search box. The timetable uses it in place of a row of chips when a business has more than
 * `CHIPS_UP_TO` people to choose from, so "Coach on each day" stays one line per day.
 *
 * The options come in the order the caller gives, under their `group` heading (the level's
 * coaches first, then everyone else). The option with value "" ("Later") stays last and is never
 * filtered out. The list is drawn over the page (a portal), so the scrolling rail never cuts it.
 */

export const CHIPS_UP_TO = 4;

/** The most the list takes: the search box and about eight people. */
const POPUP_HEIGHT = 320;

/** `photoUrl`: the person's face before the name (null draws the initials). Left out for "No one". */
export type CoachOption = { value: string; label: string; group?: string; photoUrl?: string | null };

export function CoachPicker({
  label,
  value,
  options,
  onChange,
  disabled = false,
}: {
  /** The accessible name: "coach on Monday". */
  label: string;
  value: string;
  options: CoachOption[];
  onChange: (value: string) => void;
  /** A change is on its way: the chip waits. */
  disabled?: boolean;
}) {
  const id = useId();
  const listId = `${id}-list`;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [words, setWords] = useState("");
  const [active, setActive] = useState(0);
  // Under the chip, or over it when the window has no room below.
  const [place, setPlace] = useState<{ top?: number; bottom?: number; left: number } | null>(null);
  // Where the list is drawn. Inside a modal dialog (the phone session panel) everything outside the
  // dialog is inert, so the list goes into the dialog. It is `fixed`, so its place does not change.
  const [host, setHost] = useState<Element | null>(null);
  const chosen = options.find((option) => option.value === value);

  // The name search rule, best first under each heading. "Later" (value "") stays last.
  const found = useMemo(() => searchByName(options, words, (option) => (option.value === "" ? [] : [option.label])), [options, words]);
  useTrackNameSearch("coach_picker", words, found.results);
  const shown = useMemo(() => {
    if (!found.searched) return options;
    const headings = new Map<string | undefined, number>();
    for (const option of options) if (!headings.has(option.group)) headings.set(option.group, headings.size);
    const later = options.filter((option) => option.value === "");
    return [...[...found.rows].sort((a, b) => headings.get(a.group)! - headings.get(b.group)!), ...later];
  }, [found, options]);

  // Measured again on every scroll and resize while open.
  useLayoutEffect(() => {
    if (!open) return;
    setHost(buttonRef.current?.closest("dialog") ?? document.body);
    const measure = () => {
      const box = buttonRef.current?.getBoundingClientRect();
      if (!box) return;
      setPlace(window.innerHeight - box.bottom < POPUP_HEIGHT ? { bottom: window.innerHeight - box.top + 4, left: box.left } : { top: box.bottom + 4, left: box.left });
    };
    measure();
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [open]);

  // The search box takes the keys once the list is drawn; a press outside closes it.
  const drawn = open && place !== null;
  useEffect(() => {
    if (!drawn) return;
    inputRef.current?.focus();
    const onDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!popupRef.current?.contains(target) && !buttonRef.current?.contains(target)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [drawn]);

  function openList() {
    setWords("");
    setActive(Math.max(0, options.findIndex((option) => option.value === value)));
    setOpen(true);
  }

  function close() {
    setOpen(false);
    buttonRef.current?.focus();
  }

  function pick(option: CoachOption | undefined) {
    if (!option) return;
    close();
    if (option.value !== value) onChange(option.value);
  }

  const current = Math.min(active, shown.length - 1);
  const optionId = (index: number) => `${id}-option-${index}`;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={`${label}: ${chosen?.label ?? "no one yet"}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openList())}
        className="inline-flex max-w-full items-center gap-1 justify-self-start rounded-full border-[1.5px] border-ink bg-sunken px-2.5 py-1 text-[12px] font-semibold text-ink focus-visible:outline-3 focus-visible:outline-accent-soft disabled:cursor-default disabled:opacity-45"
      >
        {chosen && chosen.photoUrl !== undefined ? <Avatar name={chosen.label} photoUrl={chosen.photoUrl} size={16} /> : null}
        <span className="truncate">{chosen?.label ?? "Pick a person"}</span>
        <span aria-hidden="true" className="text-[10px] text-ink-3">
          ▾
        </span>
      </button>
      {open && place
        ? createPortal(
            <div
              ref={popupRef}
              className="fixed z-[60] grid w-60 gap-1 rounded-xl border border-line bg-paper p-1.5 shadow-card"
              style={place}
            >
              <SearchField
                inputRef={inputRef}
                label={`Find a person for the ${label}`}
                placeholder="Find a person"
                combobox={{ listId, activeId: shown.length ? optionId(current) : undefined }}
                value={words}
                onChange={(typed) => {
                  setWords(typed);
                  setActive(0);
                }}
                onKeyDown={(event) => {
                  if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                    event.preventDefault();
                    const step = event.key === "ArrowDown" ? 1 : -1;
                    setActive((current + step + shown.length) % Math.max(1, shown.length));
                  } else if (event.key === "Enter") {
                    event.preventDefault();
                    pick(shown[current]);
                  } else if (event.key === "Escape") {
                    // Closes the list only: the timetable's own Esc (stop adding) stays out of it.
                    event.preventDefault();
                    event.stopPropagation();
                    close();
                  }
                }}
              />
              <div id={listId} role="listbox" aria-label={label} className="grid max-h-[272px] overflow-y-auto">
                {shown.map((option, index) => (
                  <div key={option.value || "later"} className="grid">
                    {option.group && option.group !== shown[index - 1]?.group ? (
                      <span className="px-2 pt-1.5 pb-0.5 text-[10.5px] font-bold text-ink-3 uppercase">{option.group}</span>
                    ) : null}
                    <div
                      id={optionId(index)}
                      role="option"
                      tabIndex={-1}
                      aria-selected={option.value === value}
                      onPointerEnter={() => setActive(index)}
                      onClick={() => pick(option)}
                      className={[
                        "flex cursor-pointer items-center justify-between rounded-lg px-2 py-1.5 text-[13px]",
                        index === current ? "bg-sunken" : "",
                        option.value === value ? "font-bold text-ink" : "text-ink-2",
                        option.value === "" ? "border-t border-line" : "",
                      ].join(" ")}
                    >
                      <span className="flex min-w-0 items-center gap-1.5">
                        {option.photoUrl !== undefined ? <Avatar name={option.label} photoUrl={option.photoUrl} size={20} /> : null}
                        <span className="truncate">{option.label}</span>
                      </span>
                      {option.value === value ? <span aria-hidden="true">✓</span> : null}
                    </div>
                  </div>
                ))}
                {shown.every((option) => option.value === "") ? <span className="px-2 py-1.5 text-[12px] text-ink-3">No one by that name.</span> : null}
              </div>
            </div>,
            host ?? document.body,
          )
        : null}
    </>
  );
}
